import { useEffect, useState } from 'react';
import { ChannelToggles, describePushResult } from '../components/ChannelToggles';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import {
  ArrowLeft, Send, Calendar, Clock, Trash2, Eye, Users, CheckCircle2,
  Repeat, X, Sparkles, Mail, BookmarkPlus, Bookmark,
} from 'lucide-react';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function AdminPrayerEmails() {
  const navigate = useNavigate();

  // Compose form
  const [subject, setSubject] = useState('A prayer for your week');
  const [body, setBody] = useState('');
  const [scriptureText, setScriptureText] = useState('');
  const [scriptureRef, setScriptureRef] = useState('');

  // Targeting
  const [targetMode, setTargetMode] = useState('all'); // 'all' | 'selected'
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [members, setMembers] = useState([]);

  // Timing
  const [timing, setTiming] = useState('now'); // 'now' | 'once' | 'weekly'
  const [sendAt, setSendAt] = useState('');
  const [weekday, setWeekday] = useState(0); // Monday
  const [hourUtc, setHourUtc] = useState(13); // 9am ET

  const [sending, setSending] = useState(false);
  const [channels, setChannels] = useState({ send_email: true, send_push: false });
  const [scheduled, setScheduled] = useState([]);
  const [history, setHistory] = useState([]);

  // Preview
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Templates
  const [templates, setTemplates] = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState(null);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');

  const loadEverything = async () => {
    try {
      const [membersRes, schedRes, histRes, tplRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/prayer-emails/scheduled'),
        api.get('/admin/prayer-emails/history'),
        api.get('/admin/prayer-emails/templates'),
      ]);
      setMembers((membersRes.data || []).filter(u => u.role !== 'admin'));
      setScheduled(schedRes.data || []);
      setHistory(histRes.data || []);
      setTemplates(tplRes.data || []);
    } catch (e) {
      toast.error('Could not load members or history');
    }
  };

  useEffect(() => { loadEverything(); }, []);

  // Escape closes preview modal
  useEffect(() => {
    if (!previewOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setPreviewOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewOpen]);

  const canSend = subject.trim().length > 0 && body.trim().length > 0;

  const toggleUser = (id) => {
    setSelectedUserIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const openPreview = async () => {
    if (!body.trim()) { toast.error('Write the prayer body first'); return; }
    try {
      const res = await api.post('/admin/prayer-emails/preview', {
        body,
        scripture_text: scriptureText || null,
        scripture_ref: scriptureRef || null,
      });
      setPreviewHtml(res.data.html);
      setPreviewOpen(true);
    } catch (e) {
      toast.error('Preview failed');
    }
  };

  const buildPayload = () => ({
    subject: subject.trim(),
    body: body.trim(),
    scripture_text: scriptureText.trim() || null,
    scripture_ref: scriptureRef.trim() || null,
    user_ids: targetMode === 'selected' ? selectedUserIds : null,
    template_id: activeTemplateId || null,
    ...channels,
  });

  const loadTemplate = (tpl) => {
    setSubject(tpl.subject || '');
    setBody(tpl.body || '');
    setScriptureText(tpl.scripture_text || '');
    setScriptureRef(tpl.scripture_ref || '');
    setActiveTemplateId(tpl.id);
    toast.success(`Loaded "${tpl.name}"`);
  };

  const openSaveTemplate = () => {
    if (!canSend) { toast.error('Write a subject and body first'); return; }
    setTemplateName('');
    setShowSaveTemplate(true);
  };

  const saveTemplate = async () => {
    const name = templateName.trim();
    if (!name) { toast.error('Give this template a name'); return; }
    try {
      await api.post('/admin/prayer-emails/templates', {
        name,
        subject: subject.trim(),
        body: body.trim(),
        scripture_text: scriptureText.trim() || null,
        scripture_ref: scriptureRef.trim() || null,
      });
      toast.success(`Template "${name}" saved`);
      setShowSaveTemplate(false);
      await loadEverything();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await api.delete(`/admin/prayer-emails/templates/${tpl.id}`);
      if (activeTemplateId === tpl.id) setActiveTemplateId(null);
      toast.success('Template deleted');
      await loadEverything();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const sendNow = async () => {
    if (!canSend) return;
    if (targetMode === 'selected' && selectedUserIds.length === 0) {
      toast.error('Pick at least one member — or switch to "All members"');
      return;
    }
    const targetCount = targetMode === 'selected' ? selectedUserIds.length : members.length;
    if (!window.confirm(`Send this prayer to ${targetCount} member${targetCount === 1 ? '' : 's'} right now?`)) return;
    try {
      setSending(true);
      const res = await api.post('/admin/prayer-emails/send', buildPayload());
      const { sent, failed, total_targets, push } = res.data;
      toast.success(`${channels.send_email ? `Prayer emailed to ${sent} of ${total_targets}` : 'Prayer sent'}${describePushResult(push)}`);
      if (failed > 0) toast.error(`${failed} failed to send`);
      resetCompose();
      await loadEverything();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const schedule = async () => {
    if (!canSend) return;
    const base = buildPayload();
    let payload;
    if (timing === 'once') {
      if (!sendAt) { toast.error('Pick a date & time'); return; }
      const isoUtc = new Date(sendAt).toISOString();
      payload = { ...base, kind: 'once', send_at: isoUtc };
    } else if (timing === 'weekly') {
      payload = { ...base, kind: 'weekly', weekday, hour_utc: hourUtc };
    } else return;

    try {
      setSending(true);
      await api.post('/admin/prayer-emails/schedule', payload);
      toast.success(timing === 'once' ? 'Prayer scheduled' : 'Weekly prayer scheduled — it will keep sending until you cancel');
      resetCompose();
      await loadEverything();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Schedule failed');
    } finally {
      setSending(false);
    }
  };

  const cancelSchedule = async (id) => {
    if (!window.confirm('Cancel this scheduled prayer?')) return;
    try {
      await api.delete(`/admin/prayer-emails/scheduled/${id}`);
      toast.success('Cancelled');
      await loadEverything();
    } catch (e) {
      toast.error('Cancel failed');
    }
  };

  const resetCompose = () => {
    setBody('');
    setScriptureText('');
    setScriptureRef('');
    setSelectedUserIds([]);
    setTargetMode('all');
    setTiming('now');
    setSendAt('');
    setActiveTemplateId(null);
  };

  const fmtDt = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return iso; }
  };

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate('/admin')}
          className="mb-6 inline-flex items-center gap-2 text-sm hover:underline"
          style={{ color: '#5B7C99', fontFamily: 'Manrope, sans-serif' }}
          data-testid="prayer-emails-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </button>

        {/* Hero */}
        <div className="mb-8 p-8 rounded-3xl" style={{ background: 'linear-gradient(135deg, #E57A4415 0%, #FDFBF7 60%, #78A18E15 100%)', border: '1px solid #F0EBE0' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E57A44 0%, #B8564A 100%)' }}>
              <Sparkles className="w-6 h-6" style={{ color: '#FFFFFF' }} />
            </div>
            <div>
              <h1 className="text-3xl font-bold mb-0.5" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }} data-testid="prayer-emails-title">Prayer Emails</h1>
              <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Write a prayer and send it — now, once at a specific time, or every week — to all members or a hand-picked group.</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Compose */}
          <div className="lg:col-span-2 space-y-6">
            {/* Templates row */}
            <div className="p-5 rounded-3xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }} data-testid="templates-row">
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Bookmark className="w-4 h-4" style={{ color: '#B8891A' }} />
                  <h2 className="text-sm font-bold uppercase tracking-widest" style={{ fontFamily: 'Manrope, sans-serif', color: '#8C867D', letterSpacing: '0.1em' }}>Prayer Templates</h2>
                </div>
                <button onClick={openSaveTemplate} disabled={!canSend} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition disabled:opacity-40" style={{ backgroundColor: '#FBEFCE', color: '#8B6A0A', border: '1px solid #E8C26555', fontFamily: 'Manrope, sans-serif' }} data-testid="save-template-btn">
                  <BookmarkPlus className="w-3.5 h-3.5" /> Save current as template
                </button>
              </div>
              {templates.length === 0 ? (
                <p className="text-sm italic" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                  No templates yet — write a prayer below and tap "Save current as template" to reuse it later.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {templates.map(tpl => {
                    const active = activeTemplateId === tpl.id;
                    return (
                      <div key={tpl.id} className="inline-flex items-center rounded-full transition" style={{ backgroundColor: active ? '#FBEFCE' : 'rgba(255,255,255,0.9)', border: `1px solid ${active ? '#E8C26588' : '#E8C26555'}` }} data-testid={`template-${tpl.id}`}>
                        <button onClick={() => loadTemplate(tpl)} className="pl-3 pr-2 py-1.5 text-xs font-semibold flex items-center gap-1.5" style={{ fontFamily: 'Manrope, sans-serif', color: '#7A5C1E', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          <span>{tpl.name}</span>
                          {tpl.use_count > 0 && <span className="text-[9px] opacity-60">·{tpl.use_count}×</span>}
                        </button>
                        <button onClick={() => deleteTemplate(tpl)} className="pr-2.5 pl-1 py-1.5 opacity-50 hover:opacity-100 transition" data-testid={`template-delete-${tpl.id}`}>
                          <X className="w-3 h-3" style={{ color: '#8B6A0A' }} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="p-6 rounded-3xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
              <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Write the prayer</h2>

              <Field label="Subject line (what shows in their inbox)">
                <input value={subject} onChange={e => setSubject(e.target.value)} maxLength={140} style={inputStyle} data-testid="prayer-subject" />
              </Field>

              <Field label="Prayer body" hint="Blank lines break paragraphs. Speak from the heart.">
                <textarea value={body} onChange={e => setBody(e.target.value)} maxLength={5000} rows={8} style={{ ...inputStyle, minHeight: 180, fontFamily: 'Lora, serif', fontSize: 15, lineHeight: 1.7 }} placeholder="Father, I lift up my friend today…" data-testid="prayer-body" />
                <p className="text-xs mt-1 text-right" style={{ color: '#8C867D' }}>{body.length}/5000</p>
              </Field>

              <div className="grid md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <Field label="Scripture (optional)">
                    <input value={scriptureText} onChange={e => setScriptureText(e.target.value)} maxLength={800} placeholder='"He heals the brokenhearted…"' style={inputStyle} data-testid="prayer-scripture-text" />
                  </Field>
                </div>
                <Field label="Reference (optional)">
                  <input value={scriptureRef} onChange={e => setScriptureRef(e.target.value)} maxLength={120} placeholder="Psalm 147:3" style={inputStyle} data-testid="prayer-scripture-ref" />
                </Field>
              </div>

              <button onClick={openPreview} disabled={!body.trim()} className="mt-2 inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full transition disabled:opacity-50" style={{ backgroundColor: '#F3EFE7', color: '#2C2724' }} data-testid="prayer-preview-btn">
                <Eye className="w-4 h-4" /> Preview email
              </button>
            </div>

            {/* Targeting */}
            <div className="p-6 rounded-3xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                <Users className="w-5 h-5" style={{ color: '#78A18E' }} /> Who receives it
              </h2>
              <div className="flex gap-2 mb-4">
                <ChoicePill active={targetMode === 'all'} onClick={() => setTargetMode('all')} testid="target-all">All members ({members.length})</ChoicePill>
                <ChoicePill active={targetMode === 'selected'} onClick={() => setTargetMode('selected')} testid="target-selected">Pick individually</ChoicePill>
              </div>
              {targetMode === 'selected' && (
                <div className="max-h-64 overflow-y-auto rounded-2xl border p-2" style={{ borderColor: '#F0EBE0', backgroundColor: '#FDFBF7' }}>
                  {members.length === 0 && <p className="text-sm p-4" style={{ color: '#8C867D' }}>No members yet.</p>}
                  {members.map(m => {
                    const on = selectedUserIds.includes(m.id);
                    return (
                      <label key={m.id} className="flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer hover:bg-white transition" data-testid={`member-toggle-${m.id}`}>
                        <input type="checkbox" checked={on} onChange={() => toggleUser(m.id)} style={{ accentColor: '#E57A44' }} />
                        <span className="text-sm flex-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>{m.name}</span>
                        <span className="text-xs" style={{ color: '#8C867D' }}>{m.email}</span>
                      </label>
                    );
                  })}
                  <div className="pt-2 mt-2 border-t flex justify-between" style={{ borderColor: '#F0EBE0' }}>
                    <button onClick={() => setSelectedUserIds(members.map(m => m.id))} className="text-xs px-3 py-1 rounded-full" style={{ backgroundColor: '#F3EFE7' }}>Select all</button>
                    <button onClick={() => setSelectedUserIds([])} className="text-xs px-3 py-1 rounded-full" style={{ backgroundColor: '#F3EFE7' }}>Clear</button>
                  </div>
                </div>
              )}
            </div>

            {/* Timing */}
            <div className="p-6 rounded-3xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                <Clock className="w-5 h-5" style={{ color: '#5B7C99' }} /> When
              </h2>
              <div className="flex flex-wrap gap-2 mb-4">
                <ChoicePill active={timing === 'now'} onClick={() => setTiming('now')} testid="timing-now">Send now</ChoicePill>
                <ChoicePill active={timing === 'once'} onClick={() => setTiming('once')} testid="timing-once">Schedule once</ChoicePill>
                <ChoicePill active={timing === 'weekly'} onClick={() => setTiming('weekly')} testid="timing-weekly">Weekly recurring</ChoicePill>
              </div>

              {timing === 'once' && (
                <div>
                  <label className="block text-sm font-semibold mb-1" style={{ color: '#2C2A29' }}>Date & time (your local time)</label>
                  <input type="datetime-local" value={sendAt} onChange={e => setSendAt(e.target.value)} style={inputStyle} data-testid="schedule-datetime" />
                </div>
              )}

              {timing === 'weekly' && (
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold mb-1" style={{ color: '#2C2A29' }}>Day of week</label>
                    <select value={weekday} onChange={e => setWeekday(Number(e.target.value))} style={inputStyle} data-testid="schedule-weekday">
                      {WEEKDAYS.map((w, i) => <option key={i} value={i}>{w}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1" style={{ color: '#2C2A29' }}>Hour (UTC)</label>
                    <select value={hourUtc} onChange={e => setHourUtc(Number(e.target.value))} style={inputStyle} data-testid="schedule-hour">
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{i.toString().padStart(2, '0')}:00 UTC</option>
                      ))}
                    </select>
                    <p className="text-xs mt-1" style={{ color: '#8C867D' }}>
                      Sent every {WEEKDAYS[weekday]} at {hourUtc.toString().padStart(2, '0')}:00 UTC (that's{' '}
                      {new Date(Date.UTC(2020, 0, 6 + weekday, hourUtc, 0)).toLocaleString(undefined, { weekday: 'long', hour: 'numeric', minute: '2-digit' })} your time)
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 rounded-3xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0' }}>
              <ChannelToggles channels={channels} onChange={setChannels} testidPrefix="prayer-channel" />
            </div>

            {/* Action bar */}
            <div className="p-6 rounded-3xl flex items-center justify-between gap-4 flex-wrap" style={{ background: 'linear-gradient(135deg, #E57A4408 0%, #FDFBF7 100%)', border: '1px solid #F0EBE0' }}>
              <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                {timing === 'now' && `Ready to send to ${targetMode === 'selected' ? selectedUserIds.length : members.length} member${(targetMode === 'selected' ? selectedUserIds.length : members.length) === 1 ? '' : 's'} now.`}
                {timing === 'once' && sendAt && `Will send once on ${new Date(sendAt).toLocaleString()}`}
                {timing === 'weekly' && `Will send every ${WEEKDAYS[weekday]} at ${hourUtc.toString().padStart(2, '0')}:00 UTC until cancelled.`}
              </p>
              {timing === 'now' ? (
                <button onClick={sendNow} disabled={!canSend || sending || (!channels.send_email && !channels.send_push)} className="px-6 py-3 rounded-full text-sm font-bold inline-flex items-center gap-2 transition disabled:opacity-50 hover:-translate-y-0.5" style={{ background: 'linear-gradient(135deg, #E57A44 0%, #B8564A 100%)', color: '#FFFFFF', fontFamily: 'Manrope, sans-serif', boxShadow: '0 4px 14px rgba(229,122,68,0.35)' }} data-testid="send-now-btn">
                  <Send className="w-4 h-4" /> Send prayer now
                </button>
              ) : (
                <button onClick={schedule} disabled={!canSend || sending || (timing === 'once' && !sendAt) || (!channels.send_email && !channels.send_push)} className="px-6 py-3 rounded-full text-sm font-bold inline-flex items-center gap-2 transition disabled:opacity-50 hover:-translate-y-0.5" style={{ background: 'linear-gradient(135deg, #5B7C99 0%, #3F5B7A 100%)', color: '#FFFFFF', fontFamily: 'Manrope, sans-serif', boxShadow: '0 4px 14px rgba(91,124,153,0.35)' }} data-testid="schedule-btn">
                  <Calendar className="w-4 h-4" /> Schedule prayer
                </button>
              )}
            </div>
          </div>

          {/* Right column: scheduled + history */}
          <div className="space-y-6">
            <div className="p-6 rounded-3xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
              <h2 className="text-lg font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                <Repeat className="w-5 h-5" style={{ color: '#5B7C99' }} /> Scheduled
              </h2>
              {scheduled.length === 0 ? (
                <p className="text-sm italic" style={{ color: '#8C867D' }}>Nothing scheduled — you can send now or schedule above.</p>
              ) : (
                <ul className="space-y-3">
                  {scheduled.map(s => (
                    <li key={s.id} className="p-3 rounded-2xl" style={{ backgroundColor: '#FDFBF7', border: '1px solid #F0EBE0' }} data-testid={`sched-${s.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold truncate" style={{ color: '#2C2A29', fontFamily: 'Merriweather, serif' }}>{s.subject}</p>
                          <p className="text-xs mt-0.5" style={{ color: '#5B7C99', fontWeight: 600 }}>
                            {s.kind === 'weekly' ? `Every ${WEEKDAYS[s.weekday]} @ ${s.hour_utc.toString().padStart(2, '0')}:00 UTC` : `Once on ${fmtDt(s.send_at)}`}
                          </p>
                          <p className="text-xs mt-1" style={{ color: '#8C867D' }}>Next: {fmtDt(s.next_run_at)}</p>
                          {s.run_count > 0 && <p className="text-xs mt-0.5" style={{ color: '#78A18E' }}>Sent {s.run_count} time{s.run_count === 1 ? '' : 's'}</p>}
                        </div>
                        <button onClick={() => cancelSchedule(s.id)} className="p-1.5 rounded-full transition" style={{ backgroundColor: '#FDECEB' }} data-testid={`cancel-sched-${s.id}`}>
                          <X className="w-3.5 h-3.5" style={{ color: '#B8564A' }} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="p-6 rounded-3xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
              <h2 className="text-lg font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                <Mail className="w-5 h-5" style={{ color: '#78A18E' }} /> Recent sends
              </h2>
              {history.length === 0 ? (
                <p className="text-sm italic" style={{ color: '#8C867D' }}>No prayers sent yet.</p>
              ) : (
                <ul className="space-y-3 max-h-96 overflow-y-auto pr-2">
                  {history.map(h => (
                    <li key={h.id} className="p-3 rounded-2xl" style={{ backgroundColor: '#FDFBF7', border: '1px solid #F0EBE0' }} data-testid={`history-${h.id}`}>
                      <p className="text-sm font-bold truncate" style={{ color: '#2C2A29', fontFamily: 'Merriweather, serif' }}>{h.subject}</p>
                      <p className="text-xs mt-1" style={{ color: '#8C867D' }}>{fmtDt(h.created_at)}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: '#DFEAE1', color: '#3F5641' }}>
                          <CheckCircle2 className="w-3 h-3" /> {h.sent} sent
                        </span>
                        {h.failed > 0 && <span className="px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FDECEB', color: '#B8564A' }}>{h.failed} failed</span>}
                        <span className="px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F3EFE7', color: '#8C867D' }}>{h.source}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {previewOpen && previewHtml && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(44,42,41,0.5)' }} onClick={() => setPreviewOpen(false)}>
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl" style={{ backgroundColor: '#FDFBF7' }} onClick={e => e.stopPropagation()} data-testid="preview-modal">
            <div className="sticky top-0 flex items-center justify-between p-4 border-b" style={{ backgroundColor: '#FDFBF7', borderColor: '#F0EBE0' }}>
              <p className="text-sm font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Email preview</p>
              <button onClick={() => setPreviewOpen(false)} className="p-2 rounded-full hover:bg-[#F3EFE7]"><X className="w-4 h-4" /></button>
            </div>
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </div>
      )}

      {showSaveTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(44,42,41,0.5)' }} onClick={() => setShowSaveTemplate(false)}>
          <div className="w-full max-w-md rounded-3xl p-6" style={{ backgroundColor: '#FDFBF7' }} onClick={e => e.stopPropagation()} data-testid="save-template-modal">
            <div className="flex items-center justify-between mb-4">
              <p className="text-lg font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Save as template</p>
              <button onClick={() => setShowSaveTemplate(false)} className="p-1.5 rounded-full hover:bg-[#F3EFE7]"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm mb-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Give this prayer a short name so you can reuse it later.</p>
            <input
              autoFocus
              value={templateName}
              onChange={e => setTemplateName(e.target.value)}
              maxLength={80}
              placeholder="e.g. Prayer for peace"
              style={inputStyle}
              onKeyDown={e => { if (e.key === 'Enter') saveTemplate(); }}
              data-testid="template-name-input"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setShowSaveTemplate(false)} className="px-4 py-2 rounded-full text-sm font-semibold" style={{ backgroundColor: '#F3EFE7', color: '#2C2724' }}>Cancel</button>
              <button onClick={saveTemplate} className="px-5 py-2 rounded-full text-sm font-bold inline-flex items-center gap-2" style={{ backgroundColor: '#B8891A', color: '#FFFFFF', fontFamily: 'Manrope, sans-serif' }} data-testid="template-save-confirm">
                <BookmarkPlus className="w-4 h-4" /> Save template
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

const inputStyle = { fontFamily: 'Nunito, sans-serif', border: '1px solid #E6E2D8', borderRadius: 12, padding: '10px 14px', width: '100%', backgroundColor: '#FFFFFF', color: '#2C2A29' };

function Field({ label, hint, children }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2A29' }}>{label}</label>
      {children}
      {hint && <p className="text-xs mt-1" style={{ color: '#8C867D' }}>{hint}</p>}
    </div>
  );
}

function ChoicePill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className="px-4 py-2 rounded-full text-sm font-semibold transition"
      style={{
        backgroundColor: active ? '#E57A44' : '#F3EFE7',
        color: active ? '#FFFFFF' : '#2C2724',
        border: '1px solid ' + (active ? '#E57A44' : '#E6E2D8'),
        fontFamily: 'Manrope, sans-serif',
      }}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
